import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, EyeOff, KeyRound } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { change_Current_Location, changeHeaderTitle } from '../../Common/store';
import { terminalSelfChangePassword } from '../api/cp';
import { MANUAL_PAYMENT_ENV_DEFAULTS } from '../../Common/hooks/useManualPaymentConfig';
import DemoModeBanner from '../components/DemoModeBanner';

/**
 * 앱 설정 페이지 - 비밀번호 변경
 */
function AppSettings() {
    const strictPasswordRules = MANUAL_PAYMENT_ENV_DEFAULTS.passwordChangeMode === 'NOTICE';
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [pwdModalOpen, setPwdModalOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pwdSubmitting, setPwdSubmitting] = useState(false);
    const [pwdError, setPwdError] = useState('');
    const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

    useEffect(() => {
        dispatch(change_Current_Location('설정'));
        dispatch(changeHeaderTitle({ mainTitle: '설정', subTitle: '' }));
    }, [dispatch]);

    useEffect(() => {
        if (!pwdModalOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [pwdModalOpen]);

    useEffect(() => {
        if (!toast.visible) return;
        const t = setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2500);
        return () => clearTimeout(t);
    }, [toast.visible]);

    const closePwdModal = () => {
        setPwdModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPwdError('');
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
    };

    const handleChangePassword = async () => {
        setPwdError('');
        if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            setPwdError('모든 항목을 입력해 주세요.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwdError('새 비밀번호가 일치하지 않습니다.');
            return;
        }
        if (strictPasswordRules && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/.test(newPassword)) {
            setPwdError('새 비밀번호는 12자 이상이며 대문자, 소문자, 숫자, 특수문자를 모두 포함해야 합니다.');
            return;
        }
        if (newPassword === currentPassword) {
            setPwdError('새 비밀번호는 현재 비밀번호와 달라야 합니다.');
            return;
        }
        try {
            setPwdSubmitting(true);
            await terminalSelfChangePassword({
                currentPassword: currentPassword.trim(),
                newPassword: newPassword.trim(),
            });
            closePwdModal();
            setToast({ visible: true, message: '비밀번호가 변경되었습니다.', type: 'success' });
        } catch (e) {
            const msg =
                e?.response?.data?.msg ||
                e?.message ||
                '비밀번호 변경에 실패했습니다.';
            setPwdError(msg);
        } finally {
            setPwdSubmitting(false);
        }
    };

    const inputWrap = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        background: '#F9FAFB',
        overflow: 'hidden',
    };

    const inputBase = {
        flex: 1,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        padding: '12px 14px',
        fontSize: '15px',
        minWidth: 0,
    };

    const eyeBtn = {
        border: 'none',
        background: 'transparent',
        padding: '10px 12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: '#6B7280',
    };

    return (
        <div style={{
            height: '100dvh',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '768px',
            margin: '0 auto',
            background: '#FFFFFF',
            overflow: 'hidden',
        }}>
            <style>
                {`
                    @keyframes appSettingsSlideDownToast {
                        from {
                            opacity: 0;
                            transform: translateX(-50%) translateY(-100%);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(-50%) translateY(0);
                        }
                    }
                `}
            </style>
            {/* 헤더 */}
            <div style={{
                background: 'linear-gradient(160deg, var(--brand-gradient-start) 0%, var(--brand-gradient-mid) 60%, var(--brand-gradient-end) 100%)',
                height: '62px',
                padding: '0 12px 0 10px',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
                flexShrink: 0,
            }}>
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                    }}
                >
                    <ChevronLeft size={28} color="#FFFFFF" />
                </button>
                <h1 style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '16px',
                    fontWeight: 700,
                    margin: 0,
                }}>
                    설정
                </h1>
                <div style={{ width: 28 }} />
            </div>

            <DemoModeBanner />

            {/* 본문 */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0',
            }}>
                <button
                    type="button"
                    onClick={() => setPwdModalOpen(true)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '14px 16px',
                        background: '#FFFFFF',
                        border: 'none',
                        borderBottom: '1px solid #EAEAEA',
                        borderRadius: 0,
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#111827',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                    }}
                >
                    <KeyRound size={20} color="var(--brand-gradient-mid)" />
                    <span style={{ flex: 1 }}>비밀번호 변경</span>
                    <ChevronRight size={20} color="#9CA3AF" />
                </button>
            </div>

            {/* 비밀번호 변경 — 하단 시트 */}
            {pwdModalOpen && (
                <div
                    role="presentation"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 50,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        background: 'rgba(0,0,0,0.45)',
                    }}
                    onClick={closePwdModal}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pwd-sheet-title"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%',
                            maxWidth: '768px',
                            minWidth: 0,
                            maxHeight: 'min(92dvh, calc(100dvh - env(safe-area-inset-top, 0px) - 8px))',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            background: '#FFFFFF',
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            boxShadow: '0 -8px 24px rgba(0,0,0,0.12)',
                            padding: '20px 16px 24px',
                            paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div
                            style={{
                                width: '36px',
                                height: '4px',
                                borderRadius: '2px',
                                background: '#E5E7EB',
                                margin: '0 auto 16px',
                            }}
                        />
                        <h2
                            id="pwd-sheet-title"
                            style={{
                                fontSize: '17px',
                                fontWeight: 700,
                                margin: '0 0 16px',
                                color: '#111827',
                            }}
                        >
                            비밀번호 변경
                        </h2>

                        <label style={{ display: 'block', fontSize: '13px', color: '#6B7280', marginBottom: '6px' }}>
                            현재 비밀번호
                        </label>
                        <div style={{ ...inputWrap, marginBottom: '12px' }}>
                            <input
                                type={showCurrent ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                autoComplete="current-password"
                                style={inputBase}
                            />
                            <button type="button" style={eyeBtn} onClick={() => setShowCurrent((v) => !v)} aria-label={showCurrent ? '비밀번호 숨기기' : '비밀번호 보기'}>
                                {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        <label style={{ display: 'block', fontSize: '13px', color: '#6B7280', marginBottom: '6px' }}>
                            새 비밀번호
                        </label>
                        <div style={{ ...inputWrap, marginBottom: '12px' }}>
                            <input
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                style={inputBase}
                            />
                            <button type="button" style={eyeBtn} onClick={() => setShowNew((v) => !v)} aria-label={showNew ? '비밀번호 숨기기' : '비밀번호 보기'}>
                                {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {strictPasswordRules && (
                            <p style={{ fontSize: '12px', color: '#6B7280', margin: '-4px 0 12px' }}>12자 이상 · 영문 대문자/소문자 · 숫자 · 특수문자 포함</p>
                        )}

                        <label style={{ display: 'block', fontSize: '13px', color: '#6B7280', marginBottom: '6px' }}>
                            새 비밀번호 확인
                        </label>
                        <div style={{ ...inputWrap, marginBottom: pwdError ? '8px' : '20px' }}>
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                style={inputBase}
                            />
                            <button type="button" style={eyeBtn} onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? '비밀번호 숨기기' : '비밀번호 보기'}>
                                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        {pwdError ? (
                            <p style={{ color: '#DC2626', fontSize: '13px', margin: '0 0 16px' }}>{pwdError}</p>
                        ) : null}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={closePwdModal}
                                disabled={pwdSubmitting}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    borderRadius: '10px',
                                    border: '1px solid #E5E7EB',
                                    background: '#FFFFFF',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    cursor: pwdSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: pwdSubmitting ? 0.6 : 1,
                                }}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={handleChangePassword}
                                disabled={pwdSubmitting}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(160deg, var(--brand-gradient-start) 0%, var(--brand-gradient-mid) 100%)',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#FFFFFF',
                                    cursor: pwdSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: pwdSubmitting ? 0.7 : 1,
                                }}
                            >
                                {pwdSubmitting ? '처리 중...' : '변경'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast.visible && (
                <div
                    style={{
                        position: 'fixed',
                        top: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '768px',
                        padding: '0 16px',
                        boxSizing: 'border-box',
                        zIndex: 4000,
                        display: 'flex',
                        justifyContent: 'center',
                        animation: 'appSettingsSlideDownToast 0.3s ease-out',
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            color: '#111827',
                            fontSize: '14px',
                            fontWeight: 500,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            pointerEvents: 'auto',
                        }}
                    >
                        <div
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: toast.type === 'error' ? '#F04452' : '#3182F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <FontAwesomeIcon
                                icon={toast.type === 'error' ? faTimes : faCheck}
                                style={{ fontSize: '14px', color: '#FFFFFF' }}
                            />
                        </div>
                        <span>{toast.message}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AppSettings;
